// Typed client for POST /api/estimates/preview. Accepts the canonical
// EstimateRequest shape — widget-form / onboarding / tool adapters live
// with their surfaces, not inside this client (ADR-010).

import type { EstimateRequest, EstimateResult } from "./agent-types";
import { ApiError, apiRequest } from "./api";

export type EstimatePreviewResponse = {
  results: EstimateResult[];
};

export class EstimatePreviewError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "EstimatePreviewError";
  }
}

export class EstimateUnauthorizedError extends EstimatePreviewError {
  constructor(message = "Please sign in again to refresh your numbers.") {
    super(message);
    this.name = "EstimateUnauthorizedError";
  }
}

export class EstimateValidationError extends EstimatePreviewError {
  constructor(public details: string) {
    super(details || "validation failed");
    this.name = "EstimateValidationError";
  }
}

export class EstimateServiceUnavailableError extends EstimatePreviewError {
  constructor() {
    super("estimate service unavailable");
    this.name = "EstimateServiceUnavailableError";
  }
}

export class EstimateNetworkError extends EstimatePreviewError {
  constructor(public cause: unknown) {
    super("network error");
    this.name = "EstimateNetworkError";
  }
}

export type FetchEstimateOptions = {
  token?: string;
  signal?: AbortSignal;
};

// isAbortError detects the DOMException the browser raises when an
// AbortSignal fires — callers (client + hooks) swallow or rethrow it
// unchanged rather than treating it as a network fault.
export function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

// fetchEstimatePreview posts a canonical request to the preview endpoint.
// Errors from apiRequest (ApiError on non-2xx, network failures otherwise)
// are mapped to typed subclasses so hook consumers can branch on error
// shape without status-string parsing. The preview endpoint is public so
// callers may omit a token (the dashboard still passes one opportunistically).
export async function fetchEstimatePreview(
  req: EstimateRequest,
  opts: FetchEstimateOptions,
): Promise<EstimatePreviewResponse> {
  try {
    return await apiRequest<EstimatePreviewResponse>("/api/estimates/preview", {
      method: "POST",
      body: req,
      token: opts.token,
      signal: opts.signal,
    });
  } catch (err) {
    if (isAbortError(err)) throw err;
    if (err instanceof ApiError) {
      if (err.status === 401) {
        throw new EstimateUnauthorizedError(err.message);
      }
      if (err.status === 422 || err.status === 400) {
        throw new EstimateValidationError(err.message);
      }
      if (err.status === 503) {
        throw new EstimateServiceUnavailableError();
      }
      throw new EstimatePreviewError(err.message);
    }
    throw new EstimateNetworkError(err);
  }
}
